import { useEffect, useRef } from "react";
import { useLocation, useSearch } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { getStoredAttribution } from "@/lib/attribution";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AlertCircle, ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { TIER_PRICES_USD, TRIAL_DAYS, type PlanTier } from "@shared/schema";

type SubTier = Exclude<PlanTier, "free">;
const VALID_TIERS: SubTier[] = ["starter", "growth", "dominator"];

const TIER_NAME: Record<SubTier, string> = {
  starter: "Local Domination",
  growth: "Multi-Location Domination",
  dominator: "Market Monopoly",
};

interface SubscribeResponse {
  success: boolean;
  tier: SubTier;
  url?: string;
  sessionId?: string;
}

export default function CheckoutPage() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const { toast } = useToast();

  const tierParam = new URLSearchParams(search).get("tier") as SubTier | null;
  const tier = tierParam && VALID_TIERS.includes(tierParam) ? tierParam : null;

  const requestedTier = useRef<SubTier | null>(null);

  const createSession = useMutation({
    mutationFn: async (t: SubTier) => {
      // Forward stored ad-click attribution so the server can stamp it onto the
      // Stripe Checkout Session for offline conversion tracking. Empty for
      // organic/direct visitors — the server treats it as optional.
      const attribution = getStoredAttribution();
      const res = await apiRequest("POST", "/api/billing/subscribe", { tier: t, attribution });
      return (await res.json()) as SubscribeResponse;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/status"] });
      if (data.url) {
        // Redirect to Stripe-hosted checkout. The webhook is the source of
        // truth; on return the success URL lands the user back on /pricing.
        window.location.href = data.url;
      } else {
        toast({
          title: "Checkout unavailable",
          description: "We couldn't start checkout. Please try again in a moment.",
          variant: "destructive",
        });
      }
    },
    onError: (err: Error) => {
      toast({ title: "Checkout failed", description: err.message, variant: "destructive" });
    },
  });

  const startSession = (t: SubTier) => {
    requestedTier.current = t;
    createSession.mutate(t);
  };

  useEffect(() => {
    // Guard against React StrictMode double-invocation creating duplicate sessions.
    if (tier && requestedTier.current !== tier) {
      requestedTier.current = tier;
      createSession.mutate(tier);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier]);

  if (!tier) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="container mx-auto max-w-md py-16 px-4 text-center">
          <h1 className="text-2xl font-bold" data-testid="text-checkout-invalid">Plan not found</h1>
          <p className="text-muted-foreground mt-2">Pick a plan to continue to checkout.</p>
          <Button className="mt-6" onClick={() => setLocation("/pricing")} data-testid="button-back-to-pricing">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to plans
          </Button>
        </div>
      </div>
    );
  }

  const failed = createSession.isError;

  return (
    <div className="h-full overflow-y-auto">
      <div className="container mx-auto max-w-xl py-8 px-4">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4 -ml-2"
          onClick={() => setLocation("/pricing")}
          data-testid="button-checkout-back"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to plans
        </Button>

        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-checkout-title">
            Checkout — {TIER_NAME[tier]}
          </h1>
          <p className="text-muted-foreground mt-1">
            Start your $1 / {TRIAL_DAYS}-day trial, then {TIER_PRICES_USD[tier]}/28 days. Cancel anytime.
          </p>
        </div>

        <Card>
          <CardContent className="p-3 sm:p-4">
            {failed ? (
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-center" data-testid="status-checkout-error">
                <AlertCircle className="h-7 w-7 text-destructive" />
                <span className="text-sm text-muted-foreground">
                  We couldn't start your checkout. Please try again.
                </span>
                <Button
                  size="sm"
                  className="mt-1"
                  onClick={() => startSession(tier)}
                  disabled={createSession.isPending}
                  data-testid="button-checkout-retry"
                >
                  {createSession.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Try again
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground" data-testid="status-checkout-loading">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="text-sm">Redirecting to secure checkout…</span>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground mt-4" data-testid="text-checkout-secure">
          <ShieldCheck className="h-3.5 w-3.5" />
          Secure checkout powered by Stripe. You'll be redirected to complete payment.
        </p>
      </div>
    </div>
  );
}
